import * as ex from 'excalibur';
import { ActionType, EntityType, AnimalType, BehaviorType, EmotionType } from '../types/enums';
import { Action, AnimalConfig, GridPosition, Reaction, Metabolism } from '../types/interfaces';
import { TILE_SIZE, getAllDirections, MOVEMENT_SPEEDS } from '../config/constants';
import { Entity } from './entity';
import { Grid } from './grid';
import { EmotionSystem } from './EmotionSystem';

export class Animal extends Entity {
  public animalType: AnimalType;
  public behaviors: BehaviorType[];
  public moveSpeed: number;
  public territory?: number;
  public flockDistance?: number;
  public predators?: AnimalType[];
  public prey?: AnimalType[];
  private lastMoveTime: number = 0;
  private homeX: number;
  private homeY: number;
  
  // Reaction system properties
  public currentReaction: Reaction | null = null;
  public reactionLog: Reaction[] = [];
  
  // Metabolism properties
  public metabolism: Metabolism = {
    hunger: 0, // 0 = full, 100 = starving
    thirst: 0, // 0 = quenched, 100 = dehydrated
    energy: 100, // 0 = exhausted, 100 = energetic
    hungerRate: 0.05, // Default rates, can be overridden
    thirstRate: 0.03,
    energyRate: 0.02
  };

  constructor(options: AnimalConfig & { grid: Grid }) {
    super({
      ...options,
      type: EntityType.Animal,
      grid: options.grid
    });

    this.animalType = options.animalType;
    this.behaviors = options.behaviors || [BehaviorType.Random];
    this.moveSpeed = options.moveSpeed;
    this.territory = options.territory;
    this.flockDistance = options.flockDistance;
    this.predators = options.predators;
    this.prey = options.prey;
    
    // Set the home position (for territory-based behaviors)
    this.homeX = options.gridX;
    this.homeY = options.gridY;
    
    // Set a default emoji based on animal type (can be overridden)
    this.setAnimalEmoji();
  }
  
  // Set appropriate emoji based on animal type
  private setAnimalEmoji() {
    switch (this.animalType) {
      case AnimalType.Wolf:
        this.setEmoji('🐺');
        break;
      case AnimalType.Deer:
        this.setEmoji('🦌');
        break;
      case AnimalType.Rabbit:
        this.setEmoji('🐇');
        break;
      case AnimalType.Bear:
        this.setEmoji('🐻');
        break;
      case AnimalType.Bird:
        this.setEmoji('🐦');
        break;
      case AnimalType.Fish:
        this.setEmoji('🐟');
        break;
      default:
        this.setEmoji('❓');
    }
  }

  // Override update to handle metabolism and reactions
  public override update(engine: ex.Engine, delta: number): void {
    super.update(engine, delta);
    
    // Update metabolism
    this.updateMetabolism(delta);
  }
  
  // Update metabolism values
  private updateMetabolism(delta: number): void {
    // Convert delta to seconds (delta is in ms)
    const deltaSeconds = delta / 1000;
    
    // Increase hunger and thirst over time
    this.metabolism.hunger += this.metabolism.hungerRate * deltaSeconds;
    this.metabolism.thirst += this.metabolism.thirstRate * deltaSeconds;
    
    // Clamp values to 0-100 range
    this.metabolism.hunger = Math.min(100, Math.max(0, this.metabolism.hunger));
    this.metabolism.thirst = Math.min(100, Math.max(0, this.metabolism.thirst));
    this.metabolism.energy = Math.min(100, Math.max(0, this.metabolism.energy));
    
    // Trigger reactions based on metabolism
    this.checkMetabolismReactions();
  }
  
  // Check if we need to trigger metabolism-based reactions
  private checkMetabolismReactions(): void {
    const emotionSystem = EmotionSystem.getInstance();
    
    // Hunger reactions - trigger less frequently at higher thresholds
    if (this.metabolism.hunger > 85 && Math.random() < 0.2) {
      emotionSystem.addReaction(
        this, 
        EmotionType.Hungry, 
        3000, 
        this.metabolism.hunger / 100,
        undefined,
        "I need food!"
      );
    }
    
    // Thirst reactions - trigger less frequently at higher thresholds
    if (this.metabolism.thirst > 90 && Math.random() < 0.2) {
      emotionSystem.addReaction(
        this, 
        EmotionType.Thirsty, 
        3000, 
        this.metabolism.thirst / 100,
        undefined,
        "I need water!"
      );
    }
    
    // Energy reactions - trigger less frequently at lower thresholds
    if (this.metabolism.energy < 15 && Math.random() < 0.2) {
      emotionSystem.addReaction(
        this, 
        EmotionType.Tired, 
        4000, 
        (100 - this.metabolism.energy) / 100,
        undefined,
        "I need rest!"
      );
    }
  }

  // Check if enough time has passed for this animal to move again
  public canMove(currentTime: number): boolean {
    return currentTime - this.lastMoveTime >= this.moveSpeed;
  }

  // Called each frame to decide next action
  public getNextAction(): Action | null {
    // Reset examined cells for debug visualization
    this.examinedCells = [];
    
    // Get current time
    const currentTime = Date.now();
    
    // Check if we should move yet based on move speed
    if (!this.canMove(currentTime)) {
      return null;
    }
    
    // Decide action based on behaviors
    let action: Action | null = null;
    
    // Check each behavior in priority order
    for (const behavior of this.behaviors) {
      switch (behavior) {
        case BehaviorType.Predator:
          action = this.decidePredatorAction();
          break;
        case BehaviorType.Prey:
          action = this.decidePreyAction();
          break;
        case BehaviorType.Flock:
          action = this.decideFlockAction();
          break;
        case BehaviorType.Wander:
          action = this.decideWanderAction();
          break;
        case BehaviorType.Random:
          action = this.decideRandomAction();
          break;
        case BehaviorType.Stationary:
          action = this.createWaitAction();
          break;
      }
      
      // If we decided on an action, use it
      if (action) {
        break;
      }
    }
    
    // If no action was chosen, use random movement as fallback
    if (!action) {
      action = this.decideRandomAction();
    }
    
    // Update metabolism for movement
    if (action?.type === ActionType.Move) {
      // Decrease energy when moving
      this.metabolism.energy -= this.metabolism.energyRate * 10;
      this.lastMoveTime = currentTime;
    }
    
    return action;
  }
  
  // BEHAVIOR IMPLEMENTATIONS
  
  // Randomly move to an adjacent cell
  private decideRandomAction(): Action | null {
    // Trigger curious reaction occasionally
    if (Math.random() < 0.1) {
      EmotionSystem.getInstance().addReaction(
        this,
        EmotionType.Curious,
        1500,
        0.5,
        undefined,
        "What's around here?"
      );
    }
    
    // Get all possible moves (adjacent cells)
    const directions = getAllDirections();
    
    // Shuffle directions for randomness
    for (let i = directions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [directions[i], directions[j]] = [directions[j], directions[i]];
    }
    
    // Try each direction
    for (const dir of directions) {
      const newX = this.gridX + dir.x;
      const newY = this.gridY + dir.y;
      
      // Track examined cells for debugging
      this.examinedCells.push({ x: newX, y: newY });
      
      // Check if move is valid for this animal type
      if (this.grid.isValidMove(newX, newY, this.animalType)) {
        return this.createMoveAction(newX, newY);
      }
    }
    
    // If no valid moves, wait
    return this.createWaitAction();
  }
  
  // Move within territory radius of home position
  private decideWanderAction(): Action | null {
    // Only use this behavior if we have a territory defined
    if (this.territory === undefined) {
      return null;
    }
    
    // Trigger foraging reaction occasionally
    if (this.metabolism.hunger > 40 && Math.random() < 0.3) {
      EmotionSystem.getInstance().addReaction(
        this,
        EmotionType.Foraging,
        2000,
        this.metabolism.hunger / 100,
        undefined,
        "Looking for food"
      );
    }
    
    // Check if we're outside our territory and need to return
    const distanceFromHome = Math.abs(this.gridX - this.homeX) + Math.abs(this.gridY - this.homeY);
    
    if (distanceFromHome > this.territory) {
      // We're too far from home, try to move back toward it
      const dx = this.homeX - this.gridX;
      const dy = this.homeY - this.gridY;
      
      const moveX = this.gridX + Math.sign(dx);
      const moveY = this.gridY + Math.sign(dy);
      
      // Track examined cells
      this.examinedCells.push({ x: moveX, y: moveY });
      
      if (this.grid.isValidMove(moveX, moveY, this.animalType)) {
        return this.createMoveAction(moveX, moveY);
      }
    }
    
    // Otherwise just move randomly within territory
    return this.decideRandomAction();
  }
  
  // Find and chase prey animals
  private decidePredatorAction(): Action | null {
    // Only use this behavior if we have prey defined
    if (!this.prey || this.prey.length === 0) {
      return null;
    }
    
    // Check for prey within sight range (3 tiles)
    const entitiesInRange = this.grid.getEntitiesInRange(this.gridX, this.gridY, 3);
    
    // Track examined cells
    this.examinedCells = [
      ...this.examinedCells,
      ...this.grid.getCellsInRange(this.gridX, this.gridY, 3)
    ];
    
    // Find prey animals
    const preyAnimals = entitiesInRange.filter(entity => {
      if (entity.type !== EntityType.Animal) return false;
      const animal = entity as Animal;
      return this.prey?.includes(animal.animalType);
    });
    
    if (preyAnimals.length > 0) {
      // Sort by distance
      preyAnimals.sort((a, b) => {
        const distA = Math.abs(a.gridX - this.gridX) + Math.abs(a.gridY - this.gridY);
        const distB = Math.abs(b.gridX - this.gridX) + Math.abs(b.gridY - this.gridY);
        return distA - distB;
      });
      
      // Move toward closest prey
      const closestPrey = preyAnimals[0];
      
      // If adjacent, interact with prey (future expansion)
      const distToPrey = Math.abs(closestPrey.gridX - this.gridX) + Math.abs(closestPrey.gridY - this.gridY);
      
      if (distToPrey === 1) {
        // Trigger biting reaction
        EmotionSystem.getInstance().addReaction(
          this,
          EmotionType.Biting,
          1000,
          0.9,
          closestPrey as Animal,
          `Attacking ${(closestPrey as Animal).animalType}!`
        );
        
        return this.createInteractAction(closestPrey);
      }
      
      // Otherwise move toward prey
      const dx = closestPrey.gridX - this.gridX;
      const dy = closestPrey.gridY - this.gridY;
      
      // Add stalking reaction when pursuing prey
      EmotionSystem.getInstance().addReaction(
        this,
        EmotionType.Stalking,
        2000,
        0.8,
        closestPrey as Animal,
        `Stalking ${(closestPrey as Animal).animalType}`
      );
      
      const moveX = this.gridX + Math.sign(dx);
      const moveY = this.gridY + Math.sign(dy);
      
      if (this.grid.isValidMove(moveX, moveY, this.animalType)) {
        return this.createMoveAction(moveX, moveY);
      }
    }
    
    return null;
  }
  
  // Run away from predator animals
  private decidePreyAction(): Action | null {
    // Only use this behavior if we have predators defined
    if (!this.predators || this.predators.length === 0) {
      return null;
    }
    
    // Check for predators within sight range (4 tiles)
    const entitiesInRange = this.grid.getEntitiesInRange(this.gridX, this.gridY, 4);
    
    // Track examined cells
    this.examinedCells = [
      ...this.examinedCells,
      ...this.grid.getCellsInRange(this.gridX, this.gridY, 4)
    ];
    
    // Find predator animals
    const predatorAnimals = entitiesInRange.filter(entity => {
      if (entity.type !== EntityType.Animal) return false;
      const animal = entity as Animal;
      return this.predators?.includes(animal.animalType);
    });
    
    if (predatorAnimals.length > 0) {
      // Sort by distance
      predatorAnimals.sort((a, b) => {
        const distA = Math.abs(a.gridX - this.gridX) + Math.abs(a.gridY - this.gridY);
        const distB = Math.abs(b.gridX - this.gridX) + Math.abs(b.gridY - this.gridY);
        return distA - distB;
      });
      
      // Run away from closest predator
      const closestPredator = predatorAnimals[0];
      
      // Add frightened reaction when fleeing
      EmotionSystem.getInstance().addReaction(
        this,
        EmotionType.Frightened,
        1500,
        0.9,
        closestPredator as Animal,
        `Fleeing from ${(closestPredator as Animal).animalType}!`
      );
      
      // Find direction away from predator
      const dx = this.gridX - closestPredator.gridX;
      const dy = this.gridY - closestPredator.gridY;
      
      // Try to move in direction away from predator
      const moveX = this.gridX + Math.sign(dx);
      const moveY = this.gridY + Math.sign(dy);
      
      if (this.grid.isValidMove(moveX, moveY, this.animalType)) {
        return this.createMoveAction(moveX, moveY);
      }
      
      // If can't move directly away, try any valid move
      return this.decideRandomAction();
    } else if (Math.random() < 0.05) {
      // Add alert reaction occasionally when no predators visible
      EmotionSystem.getInstance().addReaction(
        this,
        EmotionType.Alert,
        1000,
        0.3,
        undefined,
        "Staying vigilant"
      );
    }
    
    return null;
  }
  
  // Stay near other animals of same type
  private decideFlockAction(): Action | null {
    // Only use this behavior if we have a flock distance defined
    if (this.flockDistance === undefined) {
      return null;
    }
    
    // Look for other animals of the same type within range
    const entitiesInRange = this.grid.getEntitiesInRange(this.gridX, this.gridY, 5);
    
    // Track examined cells
    this.examinedCells = [
      ...this.examinedCells,
      ...this.grid.getCellsInRange(this.gridX, this.gridY, 5)
    ];
    
    // Find other animals of same type
    const flockAnimals = entitiesInRange.filter(entity => {
      if (entity === this) return false; // Skip self
      if (entity.type !== EntityType.Animal) return false;
      const animal = entity as Animal;
      return animal.animalType === this.animalType;
    }) as Animal[];
    
    if (flockAnimals.length > 0) {
      // Calculate center of the flock
      let centerX = 0;
      let centerY = 0;
      
      for (const animal of flockAnimals) {
        centerX += animal.gridX;
        centerY += animal.gridY;
      }
      
      centerX = Math.round(centerX / flockAnimals.length);
      centerY = Math.round(centerY / flockAnimals.length);
      
      // Calculate distance to flock center
      const distanceToCenter = Math.abs(this.gridX - centerX) + Math.abs(this.gridY - centerY);
      
      // If too far from flock, move toward center
      if (distanceToCenter > this.flockDistance) {
        const dx = centerX - this.gridX;
        const dy = centerY - this.gridY;
        
        const moveX = this.gridX + Math.sign(dx);
        const moveY = this.gridY + Math.sign(dy);
        
        if (this.grid.isValidMove(moveX, moveY, this.animalType)) {
          return this.createMoveAction(moveX, moveY);
        }
      }
      
      // If too close to other animals, maintain distance
      const closestAnimal = flockAnimals.sort((a, b) => {
        const distA = Math.abs(a.gridX - this.gridX) + Math.abs(a.gridY - this.gridY);
        const distB = Math.abs(b.gridX - this.gridX) + Math.abs(b.gridY - this.gridY);
        return distA - distB;
      })[0];
      
      const distanceToClosest = Math.abs(this.gridX - closestAnimal.gridX) + 
                                Math.abs(this.gridY - closestAnimal.gridY);
      
      if (distanceToClosest < 2) {
        // Try to move away slightly
        const dx = this.gridX - closestAnimal.gridX;
        const dy = this.gridY - closestAnimal.gridY;
        
        const moveX = this.gridX + Math.sign(dx);
        const moveY = this.gridY + Math.sign(dy);
        
        if (this.grid.isValidMove(moveX, moveY, this.animalType)) {
          return this.createMoveAction(moveX, moveY);
        }
      }
    }
    
    return null;
  }
  
  // ACTION CREATION HELPERS
  
  // Create a move action
  protected createMoveAction(targetX: number, targetY: number): Action {
    return {
      type: ActionType.Move,
      entity: this,
      target: { x: targetX, y: targetY },
      execute: () => {
        console.log(`${this.animalType} moves from (${this.gridX},${this.gridY}) to (${targetX},${targetY})`);
        this.moveToGrid(targetX, targetY);
      }
    };
  }
  
  // Create a wait action
  protected createWaitAction(): Action {
    // Chance to trigger sleeping reaction when resting
    if (this.metabolism.energy < 30 && Math.random() < 0.3) {
      EmotionSystem.getInstance().addReaction(
        this,
        EmotionType.Sleeping,
        3000,
        0.7,
        undefined,
        "Resting to regain energy"
      );
      
      // Regain some energy when resting
      this.metabolism.energy += 5;
    }
    
    return {
      type: ActionType.Wait,
      entity: this,
      execute: () => {
        console.log(`${this.animalType} waits`);
      }
    };
  }
  
  // Create an interact action
  protected createInteractAction(target: Entity): Action {
    return {
      type: ActionType.Interact,
      entity: this,
      target: { x: target.gridX, y: target.gridY },
      execute: () => {
        console.log(`${this.animalType} interacts with entity at (${target.gridX},${target.gridY})`);
        // Future: Implement specific interactions (eating, mating, etc.)
      }
    };
  }
}